import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invoicesAPI } from '../services/api';

const InvoiceEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [formData, setFormData] = useState({
    hourly_rate: 0,
    payment_type: 'invoice',
    notes: '',
    discount: 0, // Rabatt in Prozent
    no_tax: false
  });

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      try {
        const response = await invoicesAPI.getById(id);
        const invoiceData = response.data.invoice;
        console.log("Geladene Rechnungsdaten:", invoiceData); // Debug-Ausgabe
        setInvoice(invoiceData);
        
        // Formular mit Daten befüllen
        setFormData({
          hourly_rate: invoiceData.hourly_rate || 0,
          payment_type: invoiceData.payment_type || 'invoice',
          notes: invoiceData.notes || '',
          discount: invoiceData.discount || 0, // Rabatt aus den Rechnungsdaten laden
          no_tax: invoiceData.no_tax || false,
          travel_allowance: invoiceData.travel_allowance || 0
        });
        
        console.log("Formular initialisiert mit:", {
          discount: invoiceData.discount || 0,
          hourly_rate: invoiceData.hourly_rate || 0,
          no_tax: invoiceData.no_tax || false
        }); // Debug der wichtigsten Werte
        
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Rechnung:', err);
        setError('Die Rechnung konnte nicht geladen werden: ' + 
                 (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInvoice();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateTotal = () => {
    if (!invoice) return { subtotal: 0, tax: 0, total: 0, discountAmount: 0 };
    
    // Ursprüngliche Zwischensumme berechnen (vor Rabatt)
    const originalSubtotal = (parseFloat(invoice.labor_cost) || 0) + 
                           (parseFloat(invoice.travel_allowance) || 0) + 
                           (parseFloat(invoice.material_cost) || 0);
    
    // Rabatt berechnen
    const discountRate = parseFloat(formData.discount) / 100;
    const discountAmount = originalSubtotal * discountRate;
    
    // Betrag nach Rabatt
    const discountedSubtotal = originalSubtotal - discountAmount;
    
    // MwSt. berechnen
    const taxRate = formData.no_tax ? 0 : 0.077;
    const tax = discountedSubtotal * taxRate;
    
    // Gesamtbetrag
    const total = discountedSubtotal + tax;
    
    return {
      originalSubtotal,
      subtotal: discountedSubtotal,
      tax,
      total,
      discountAmount
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const calculation = calculateTotal();
      
      // Daten für die Aktualisierung vorbereiten
      const updateData = {
        hourly_rate: parseFloat(formData.hourly_rate),
        payment_type: formData.payment_type,
        notes: formData.notes,
        no_tax: formData.no_tax,
        travel_allowance: parseFloat(formData.travel_allowance) || 0,
        subtotal: calculation.subtotal.toFixed(2),
        tax: calculation.tax.toFixed(2),
        total: calculation.total.toFixed(2),
        discount: parseFloat(formData.discount) || 0
      };
      
      console.log("Sende Aktualisierungsdaten an das Backend:", updateData);
      
      // Rechnung aktualisieren
      await invoicesAPI.update(id, updateData);
      
      alert('Rechnung erfolgreich aktualisiert!');
      navigate(`/rechnungen/${id}`);
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Rechnung:', err);
      setError('Fehler beim Aktualisieren der Rechnung: ' + 
               (err.response?.data?.error || err.message));
    }
  };

  // Für die Anzeige formatieren
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return 'CHF 0.00';
    return 'CHF ' + parseFloat(amount).toFixed(2);
  };

  if (loading) {
    return <div className="text-center py-10">Lade Rechnungsdaten...</div>;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
        <button 
          onClick={() => navigate(`/rechnungen/${id}`)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
        >
          Zurück zur Rechnung
        </button>
      </div>
    );
  }

  if (!invoice) {
    return <div className="text-center py-10">Rechnung nicht gefunden</div>;
  }

  const calculation = calculateTotal();

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">Rechnung #{id} bearbeiten</h1>
      
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-medium mb-4">Rechnungsinformationen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-600">Kunde</p>
            <p className="font-medium">{invoice.company_name || `${invoice.first_name} ${invoice.last_name}`}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Projekt</p>
            <p className="font-medium">{invoice.project_title}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Rechnung-Nummer</p>
            <p className="font-medium">{invoice.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Datum</p>
            <p className="font-medium">{new Date(invoice.created_at).toLocaleDateString('de-DE')}</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-medium mb-4">Rechnungsdetails bearbeiten</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Stundenansatz (CHF)</label>
            <input
              type="number"
              name="hourly_rate"
              value={formData.hourly_rate}
              onChange={handleChange}
              min="1"
              step="1"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Anfahrtspauschale (CHF)</label>
            <input
              type="number"
              name="travel_allowance"
              value={formData.travel_allowance}
              onChange={handleChange}
              min="0"
              step="0.05"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Rabatt (%)</label>
            <input
              type="number"
              name="discount"
              value={formData.discount}
              onChange={handleChange}
              min="0"
              max="100"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Geben Sie den Rabatt in Prozent an (z.B. 10 für 10%)
            </p>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Zahlungsart</label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="payment_type"
                  value="cash"
                  checked={formData.payment_type === 'cash'}
                  onChange={handleChange}
                  className="form-radio h-4 w-4 text-primary-600"
                />
                <span className="ml-2">Bar bezahlt</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="payment_type"
                  value="invoice"
                  checked={formData.payment_type === 'invoice'}
                  onChange={handleChange}
                  className="form-radio h-4 w-4 text-primary-600"
                />
                <span className="ml-2">Rechnung (zahlbar innert 20 Tagen)</span>
              </label>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="no_tax"
                checked={formData.no_tax}
                onChange={handleChange}
                className="form-checkbox h-4 w-4 text-primary-600"
              />
              <span className="ml-2">Keine MwSt. berechnen</span>
            </label>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Bemerkungen (optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            ></textarea>
          </div>
          
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-lg font-medium mb-4">Zusammenfassung</h3>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span>Ursprünglicher Betrag:</span>
                <span>{formatAmount(calculation.originalSubtotal)}</span>
              </div>
              {parseFloat(formData.discount) > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Rabatt ({formData.discount}%):</span>
                  <span>- {formatAmount(calculation.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium">
                <span>Zwischensumme:</span>
                <span>{formatAmount(calculation.subtotal)}</span>
              </div>
              {!formData.no_tax && (
                <div className="flex justify-between">
                  <span>MwSt. (7.7%):</span>
                  <span>{formatAmount(calculation.tax)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold">
                <span>Gesamtbetrag:</span>
                <span>{formatAmount(calculation.total)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(`/rechnungen/${id}`)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded"
            >
              Änderungen speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceEditPage; 